// Data service for the family tree application

import { ApiClient } from './ApiClient';
import { 
  FamilyTree, 
  Individual, 
  FamilyUnit, 
  ChildRelationship,
  EmotionalRelationship,
  Gender,
  RelationshipType,
  ParentalType,
  EmotionalTag
} from '../models/types';

// Get family tree data from API
const getFamilyTree = async (): Promise<FamilyTree> => {
  try {
    return await ApiClient.getFamilyTree();
  } catch (error) {
    console.error('Error fetching family tree data:', error);
    
    // Return an empty family tree structure as fallback
    return {
      id: 'main-tree',
      name: 'My Family Tree',
      description: 'A family tree created with the Family Tree Application',
      individuals: [],
      familyUnits: [],
      childRelationships: [],
      emotionalRelationships: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }
};

// Individual CRUD operations
const addIndividual = async (individual: Omit<Individual, 'id'>): Promise<Individual> => {
  return await ApiClient.addIndividual(individual);
};

const getIndividual = async (id: string): Promise<Individual | undefined> => {
  try {
    const individual = await ApiClient.getIndividual(id);
    return individual;
  } catch (error) {
    console.error(`Error fetching individual ${id}:`, error);
    return undefined;
  }
};

const updateIndividual = async (id: string, updates: Partial<Individual>): Promise<Individual | undefined> => {
  try {
    const individual = await ApiClient.updateIndividual(id, updates);
    return individual;
  } catch (error) {
    console.error(`Error updating individual ${id}:`, error);
    return undefined;
  }
};

const deleteIndividual = async (id: string): Promise<boolean> => {
  try {
    return await ApiClient.deleteIndividual(id);
  } catch (error) {
    console.error(`Error deleting individual ${id}:`, error);
    return false;
  }
};

// Family Unit CRUD operations
const addFamilyUnit = async (familyUnit: Omit<FamilyUnit, 'id'>): Promise<FamilyUnit> => {
  return await ApiClient.addFamilyUnit(familyUnit);
};

const getFamilyUnit = async (id: string): Promise<FamilyUnit | undefined> => {
  try {
    const unit = await ApiClient.getFamilyUnit(id);
    return unit;
  } catch (error) {
    console.error(`Error fetching family unit ${id}:`, error);
    return undefined;
  }
};

const updateFamilyUnit = async (id: string, updates: Partial<FamilyUnit>): Promise<FamilyUnit | undefined> => {
  try {
    const unit = await ApiClient.updateFamilyUnit(id, updates);
    return unit;
  } catch (error) {
    console.error(`Error updating family unit ${id}:`, error);
    return undefined;
  }
};

const deleteFamilyUnit = async (id: string): Promise<boolean> => {
  try {
    return await ApiClient.deleteFamilyUnit(id);
  } catch (error) {
    console.error(`Error deleting family unit ${id}:`, error);
    return false;
  }
};

// Child Relationship operations
const addChildRelationship = async (relationship: Omit<ChildRelationship, 'id'>): Promise<ChildRelationship> => {
  return await ApiClient.addChildRelationship(relationship);
};

// Emotional Relationship operations
const addEmotionalRelationship = async (relationship: Omit<EmotionalRelationship, 'id'>): Promise<EmotionalRelationship> => {
  return await ApiClient.addEmotionalRelationship(relationship);
};

// Generate sample data
const generateSampleData = async (): Promise<void> => {
  try {
    await ApiClient.generateSampleData();
    console.log('Sample data generated successfully');
  } catch (error) {
    console.error('Error generating sample data:', error);
    throw error;
  }
};

// Export the data service
export const DataService = {
  getFamilyTree,
  addIndividual,
  getIndividual,
  updateIndividual,
  deleteIndividual,
  addFamilyUnit,
  getFamilyUnit,
  updateFamilyUnit,
  deleteFamilyUnit,
  addChildRelationship,
  addEmotionalRelationship,
  generateSampleData
};
